import React, { useEffect, useState } from 'react';
/**
 * CalendarContent – Nathan's appointment schedule inline (HAL-2083), with no
 * email step before it (Google's own form asks for name and email at
 * confirmation). Reached from the form's "Book a 15m call instead", the
 * confirmation's "Book a 15m call", and the report's "Book a 15m call" link.
 * Google's frame cannot be restyled, so this owns only the framing: the shared
 * header row, loading and slow-load overlay, and the footer band.
 *
 * The footer's way back is an outline pill, not a link: a link styled like
 * "New tab" was what made the request path hard to find. It stays outlined
 * rather than amber-filled because picking a time is this view's main task.
 *
 * Approved designs: `.claude/design-specs/2026-11-01-talk-to-us-inline-calendar.html`,
 * `.claude/design-specs/2026-11-01-talk-to-us-note-first.html`.
 *
 * @module
 */

import {
  BOOKING_EMBED_URL,
  BOOKING_URL,
} from '@/features/ai-visibility-checker/components/booking';
import { ArrowOutIcon } from '@/components/icons/ArrowOutIcon';
import { MailIcon } from './BookingModal.icons';
import { ModalFooter, ModalHeader } from './BookingModal.anatomy';
import { CALENDAR_MESSAGE_LABEL } from '@/lib/conversion-copy';

/** How long the frame may take before the slow-load message offers Google's page. */
export const CALENDAR_SLOW_LOAD_MS = 8000;

export const CALENDAR_TITLE = 'Pick a time to talk with Nathan, Halcy’s founder';

interface CalendarContentProps {
  /** "Message Nathan instead": back to the form, the report's form on a report. */
  onMessage: () => void;
}

export function CalendarContent({ onMessage }: CalendarContentProps): React.ReactElement {
  const [loaded, setLoaded] = useState(false);
  const [slow, setSlow] = useState(false);

  // The frame stays mounted after the timeout, so a late load still replaces the message.
  useEffect(() => {
    if (loaded) return;
    const timer = setTimeout(() => setSlow(true), CALENDAR_SLOW_LOAD_MS);
    return () => clearTimeout(timer);
  }, [loaded]);

  return (
    <>
      <ModalHeader title={CALENDAR_TITLE} focusable />

      <div className="relative min-h-0 flex-1 bg-white">
        {/* Inset so Google's text column lands on the title's 20px / 32px line. */}
        <iframe
          title="Book a time with Nathan (Google Calendar)"
          src={BOOKING_EMBED_URL}
          onLoad={() => setLoaded(true)}
          className={`absolute inset-y-0 left-1 h-full w-[calc(100%-8px)] border-0 transition-opacity duration-[250ms] ease-in-out motion-reduce:transition-none sm:left-4 sm:w-[calc(100%-32px)] ${
            loaded ? 'opacity-100' : 'opacity-0'
          }`}
        />

        {!loaded && (
          <div className="absolute inset-0 grid place-items-center bg-white" data-testid="calendar-placeholder">
            <CalendarGhost />
            <div
              className="relative grid max-w-xs justify-items-center gap-3 rounded-xl bg-white/90 px-6 py-5 text-center"
              role="status"
              aria-live="polite"
            >
              {slow ? (
                <>
                  <p className="text-sm text-slate-600">The calendar is taking a while to load.</p>
                  <a
                    href={BOOKING_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex h-11 items-center gap-2 rounded-full bg-[#B45309] px-[22px] text-sm font-semibold text-white transition-colors duration-150 hover:bg-[#92400E] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B45309] focus-visible:ring-offset-2"
                  >
                    Open the calendar
                    <ArrowOutIcon className="h-3.5 w-3.5" />
                  </a>
                </>
              ) : (
                <>
                  <span
                    aria-hidden="true"
                    className="h-5 w-5 animate-spin rounded-full border-2 border-slate-200 border-t-[#B45309] motion-reduce:animate-none"
                  />
                  <p className="text-sm text-slate-600">Loading Nathan’s calendar…</p>
                </>
              )}
            </div>
          </div>
        )}

        {/* Overflow reads as a scroll, not a cut: the slot list fades into the footer hairline. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 bottom-0 z-[2] h-8 bg-gradient-to-b from-white/0 to-white"
        />
      </div>

      {/* Stacked up to 767px, the pill full width; side by side from md up. */}
      <ModalFooter className="flex-col items-stretch gap-2 px-4 pb-3.5 pt-3 md:flex-row md:items-center md:justify-between md:gap-4 md:px-8 md:py-2">
        <button
          type="button"
          onClick={onMessage}
          className="inline-flex min-h-11 items-center justify-center gap-2 whitespace-nowrap rounded-full border border-[#B45309] bg-white px-5 text-sm font-semibold text-slate-900 transition-colors duration-200 hover:border-[#92400E] hover:bg-[#FFF7ED] focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-[#B45309] motion-reduce:transition-none"
        >
          <MailIcon className="h-4 w-4 flex-none text-[#B45309]" />
          {CALENDAR_MESSAGE_LABEL}
        </button>
        <a
          href={BOOKING_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Open the calendar in a new tab"
          className="inline-flex shrink-0 items-center gap-1.5 self-center whitespace-nowrap text-[13px] font-medium text-slate-600 underline decoration-current decoration-1 underline-offset-4 transition-[color,text-decoration-color] duration-200 hover:text-slate-900 hover:decoration-transparent focus:outline-none focus-visible:rounded focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#B45309] md:text-sm"
        >
          Open in a new tab
          <ArrowOutIcon className="h-3.5 w-3.5 text-[#B45309]" />
        </a>
      </ModalFooter>
    </>
  );
}

/** A faint, static outline of Google's layout while its frame loads. */
function CalendarGhost(): React.ReactElement {
  return (
    <div
      aria-hidden="true"
      className="absolute inset-0 grid grid-cols-1 content-start gap-6 px-5 py-6 opacity-[0.55] sm:grid-cols-[220px_1fr] sm:gap-x-12 sm:px-8 sm:py-7"
    >
      <div className="col-span-full flex items-center gap-3.5">
        <span className="h-8 w-8 rounded-full bg-slate-100" />
        <span className="h-3 w-36 rounded-md bg-slate-100" />
      </div>
      <div className="grid grid-cols-7 content-start gap-2.5">
        {Array.from({ length: 28 }, (_, i) => (
          <i key={i} className="aspect-square rounded-full bg-slate-100" />
        ))}
      </div>
      <div className="hidden content-start justify-items-end gap-2.5 sm:grid">
        {Array.from({ length: 5 }, (_, i) => (
          <i key={i} className="h-10 w-40 rounded-full border border-slate-200" />
        ))}
      </div>
    </div>
  );
}
